import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@ui-kitten/components';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../Config/constants';
import themeStyle from '../../../Theme/styles';

import Badge from '../../Badge';
import Button from '../../Button';
import { formikNames, modalType } from '../../Reservation/constants';
import ResponsiveText from '../../ResponsiveText';
import Search from '../../Search';

import {
  dietaryOrAllergySamples,
  occasionSamples,
  otherLimit,
} from './constants';
import DietaryCounter from './DietaryCounter';
import { RenderProps, SelectableBadgeItem } from './types';

type SelectableBadgeProps = {
  isSelected: boolean;
  valuePrefix?: string;
  value: SelectableBadgeItem;
  values: SelectableBadgeItem[];
  onClick: (values: SelectableBadgeItem[], isRemove: boolean) => void;
};

const SelectableBadge = ({
  isSelected,
  valuePrefix = '',
  value,
  values,
  onClick,
}: SelectableBadgeProps) => (
  <Button
    onPress={() => {
      if (isSelected) {
        onClick(
          values.filter((e) => e.label !== value.label),
          true,
        );
      } else {
        onClick([...values, value], false);
      }
    }}
    plain
  >
    <View
      style={[
        themeStyle.spacingTopMedium,
        themeStyle.spacingLeftSmall,
        themeStyle.spacingRightSmall,
      ]}
    >
      {/* @ts-ignore: Badge is js file, no more props is required but still requiring some */}
      <Badge
        category={
          isSelected ? Badge.CATEGORIES.PRIMARY : Badge.CATEGORIES.BASIC
        }
        type={Badge.TYPES.SOLID}
        style={[
          { borderRadius: 25 },
          themeStyle.capitalize,
          themeStyle.pageHorizontalSpacingSmall,
        ]}
      >
        {`${valuePrefix}${value.label}`}
      </Badge>
    </View>
  </Button>
);

const DietaryOrCelebrationModalRender = ({
  value,
  type,
  onSelect,
}: RenderProps) => {
  const theme = useTheme();

  const [selectedSamples, setSelectedSamples] = useState<SelectableBadgeItem[]>(
    value || [],
  );
  const [searchedValue, setSearched] = useState<SelectableBadgeItem>();

  const isOccasions = type === modalType.CELEBRATIONS;
  const isDietaryOrAllergy = type === modalType.DIETARYORALLERGY;

  const memoizedValue = useMemo(() => value || [], [value]);

  const sampleData = useMemo(
    () => (isOccasions ? occasionSamples : dietaryOrAllergySamples),
    [isOccasions],
  );

  const filteredSamples = useMemo(() => {
    let result = sampleData;
    if (searchedValue) {
      // search the input value on sampleData
      result = sampleData.reduce((results, sample) => {
        const matchingData = sample.data.filter((item) =>
          item.label.toLowerCase().includes(searchedValue.label.toLowerCase()),
        );
        if (matchingData.length > 0) {
          results.push({ ...sample, data: matchingData });
        }
        return results;
      }, []);
    } else if (!lodashIsEmpty(memoizedValue)) {
      // filter out the value that is in sampleData
      const filteredValue = lodashFilter(memoizedValue, (v) => {
        const res = lodashFind(sampleData, (sd) =>
          sd.data.some((d) => d.label === v.label),
        );
        return lodashIsEmpty(res);
      });
      result = [
        ...sampleData,
        { label: 'Others', limit: otherLimit, data: filteredValue },
      ];
    }
    return lodashFilter(result, (r) => r.data.length);
  }, [memoizedValue, sampleData, searchedValue]) as {
    label: string;
    limit: number;
    data: SelectableBadgeItem[];
  }[];

  const _setFormikValue = () => {
    const name = isOccasions
      ? formikNames.CELEBRATIONS
      : formikNames.DIETARYORALLERGY;
    onSelect({ name, value: selectedSamples });
    setSearched(undefined);
  };

  const _toggleSelectedSamples: SelectableBadgeProps['onClick'] = useCallback(
    (values, isRemove) => {
      if (isRemove) {
        setSelectedSamples(values);
      } else {
        // the selected one is always the last one
        const selectedValue = values[values.length - 1];
        // Find the category that contains the current selection
        const category = lodashFind(filteredSamples, (fs) =>
          fs.data.some((d) => d.label === selectedValue.label),
        );
        let countInCategory = 0,
          limit = 0;
        if (category) {
          // Count how many items from this category are already selected
          countInCategory = values
            .slice(0, -1) // for excluding the currently selected
            .filter((item) =>
              category.data.some((catItem) => catItem.label === item.label),
            ).length;
          limit = category.limit;
        } else {
          const categorizedValues = new Set(
            sampleData.flatMap((sd) => sd.data.map((item) => item.label)),
          );
          // Count how many items from 'Others' category are already selected
          countInCategory =
            values.filter((val) => !categorizedValues.has(val.label)).length -
            1; // for excluding the currently selected
          limit = otherLimit;
        }
        // only update the state if still less than limit
        if (countInCategory < limit) {
          setSelectedSamples(values);
        }
      }
    },
    [filteredSamples, sampleData],
  );

  const _updateDietaryCount = useCallback((data) => {
    setSelectedSamples((prev) => {
      if (data.count === 0) {
        return lodashFilter(prev, (p) => p.label !== data.label);
      } else {
        return prev.map((p) => {
          if (p.label === data.label) {
            return { ...p, count: data.count };
          } else {
            return p;
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    setSelectedSamples(memoizedValue);
  }, [memoizedValue]);

  return (
    <View>
      {/* Search field */}
      <View style={!constants.isWeb && themeStyle.spacingBottom}>
        <Search
          fill={theme['color-info-500']}
          placeholder={`Search for ${
            isOccasions ? 'Occasions' : 'Allergies'
          }...`}
          onSearch={(s) => {
            setSearched(s ? { label: s, count: 1 } : undefined);
          }}
        />
      </View>
      {/* Searched value that is not on sampleData */}
      {Boolean(searchedValue) && (
        <View style={themeStyle.alignItemsStart}>
          <SelectableBadge
            isSelected={
              !lodashIsEmpty(
                lodashFilter(
                  selectedSamples,
                  (s) => s.label === searchedValue.label,
                ),
              )
            }
            valuePrefix="Add: "
            value={searchedValue}
            values={selectedSamples}
            onClick={_toggleSelectedSamples}
          />
        </View>
      )}
      {/* Sample and value */}
      {!lodashIsEmpty(filteredSamples) && (
        <View
          style={[
            themeStyle.flexWrap,
            themeStyle.flexDirectionRow,
            styles.marginOffset,
          ]}
        >
          {/* Render sampleData and selected value */}
          {filteredSamples.map((sample, index) => (
            <View
              key={sample.label}
              style={[
                themeStyle.flex1,
                styles.sampleItemContainer,
                // for style of first index
                index === 0 && {
                  ...styles.rightBorder,
                  borderRightColor: theme['color-basic-400'],
                },
                // for style of defined allergy/preference
                index <= 1 &&
                  filteredSamples.length === 3 && {
                    ...styles.bottomBorder,
                    borderBottomColor: theme['color-basic-400'],
                  },
                // for user search
                sample.label === 'Others' && styles.sampleOtherItemContainer,
              ]}
            >
              {Boolean(sample.label) && (
                <ResponsiveText
                  category="s2"
                  style={themeStyle.pageHorizontalSpacingSmall}
                >
                  {sample.label}
                </ResponsiveText>
              )}
              <View
                style={[themeStyle.flexWrap, themeStyle.flexDirectionRowCenter]}
              >
                {sample.data.map((da) => (
                  <View key={da.label} style={themeStyle.alignItemsStart}>
                    <SelectableBadge
                      isSelected={selectedSamples.some(
                        (s) => s.label === da.label,
                      )}
                      value={da}
                      values={selectedSamples}
                      onClick={_toggleSelectedSamples}
                    />
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      )}
      {isDietaryOrAllergy && (
        <DietaryCounter
          value={selectedSamples}
          onChange={_updateDietaryCount}
        />
      )}
      {/* Save Button */}
      <View style={[themeStyle.alignItemsCenter, themeStyle.spacingTop]}>
        <Button status="info" onPress={_setFormikValue}>
          Save details
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  marginOffset: { marginHorizontal: -5 },
  rightBorder: { borderRightWidth: 1 },
  bottomBorder: { borderBottomWidth: 1 },
  sampleItemContainer: {
    ...themeStyle.pageVerticalSpacingMedium,
  },
  sampleOtherItemContainer: {
    flexBasis: '100%',
  },
});

export default DietaryOrCelebrationModalRender;
